"use client";
import React, { useRef, useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import { X, ArrowRight, Check } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  name?: string;
  phoneNo?: string;
  general?: string;
}

export default function AuthModal({
  isOpen,
  onClose,
  onLoginSuccess,
}: AuthModalProps) {
  const [mode, setMode] = useState<"login" | "signup" | "forgot" | "reset">("login");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const modalRef = useRef<HTMLDivElement>(null);

  // Form states
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    name: "",
    phoneNo: "",
    email: "",
    password: "",
  });
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetData, setResetData] = useState({
    code: "",
    password: "",
    confirm: "",
  });

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  useEffect(() => {
    setErrors({});
    setTouched({});
  }, [mode]);

  // ------------------- Logics (Unchanged) -------------------
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLoginData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSignupData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(loginData),
      });
      const result: any = await res.json();
      if (result?.success) {
        localStorage.setItem("userLoggedIn", "true");
        toast.success("Logged in successfully!");
        setTimeout(() => window.location.reload(), 400);
      } else {
        toast.error(result?.message || "Login failed.");
      }
    } catch (err) {
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(signupData),
      });
      const result: any = await res.json();
      if (result?.success) {
        localStorage.setItem("userLoggedIn", "true");
        localStorage.setItem("isFirstOrder", "true");
        toast.success("Account created!");
        onLoginSuccess();
        onClose();
      } else {
        toast.error(result?.message || "Signup failed.");
      }
    } catch (error: any) {
      toast.error("Unexpected error.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error("Email is required");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const result: any = await res.json();
      if (result.success) {
        toast.success(result.message);
        setForgotEmail("");
      } else toast.error(result.message || "Request failed");
    } catch {
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetData.password || resetData.password !== resetData.confirm) {
      return toast.error("Passwords must match");
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: resetData.code,
          password: resetData.password,
          passwordConfirmation: resetData.confirm,
        }),
      });
      const result: any = await res.json();
      if (result.success) {
        toast.success(result.message || "Password reset successful!");
        setMode("login");
      } else toast.error(result.message || "Reset failed");
    } catch {
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Variants -------------------
  const contentVariants = {
    enter: { x: -10, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: 10, opacity: 0 },
  };

  // ------------------- Return -------------------
  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center px-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />

        <div
          ref={modalRef}
          className="relative bg-white border-t-4 border-black w-full max-w-md overflow-hidden shadow-2xl transition-all"
        >
          {/* Header Accent - Improved contrast */}
          <div className="absolute top-4 left-6">
            <p className="text-[9px] font-black uppercase tracking-[0.5em] text-gray-500">
              Identity / <span style={{ color: "var(--secondary)" }} className="font-black">Portal</span>
            </p>
          </div>

          {/* Close Button - Higher contrast */}
          <button 
            onClick={onClose} 
            className="absolute top-0 right-0 p-4 text-gray-700 hover:text-black hover:bg-gray-100 transition-colors border-l border-b border-gray-300"
          >
            <X size={18} className="stroke-2" />
          </button>

          {/* Tabs - Maximum contrast */}
          <div className="flex pt-16 border-b border-gray-300">
            {["login", "signup"].map((tab) => (
              <button
                key={tab}
                className={`flex-1 py-4 text-[11px] font-black uppercase tracking-widest transition-all ${
                  mode === tab 
                    ? "text-black border-b-2 border-black font-extrabold" 
                    : "text-gray-400 hover:text-gray-700"
                }`}
                onClick={() => setMode(tab as any)}
              >
                {tab === "login" ? "Boutique Login" : "Create Account"}
              </button>
            ))}
          </div>

          {/* Animated Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={mode}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="p-8 md:p-10"
            >
              {/* ✅ LOGIN - Normal case inputs */}
              {mode === "login" && (
                <form onSubmit={handleLogin} className="space-y-6">
                  <div className="space-y-4">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={loginData.email}
                      onChange={handleLoginChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium transition-all placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="password"
                      name="password"
                      placeholder="Password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium transition-all placeholder:text-gray-400 text-black"
                    />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      className="text-xs font-medium uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
                      onClick={() => setMode("forgot")}
                    >
                      Forgot credentials?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="group w-full py-5 bg-black text-white text-xs font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 hover:bg-[var(--secondary)] transition-all duration-500 disabled:opacity-50"
                  >
                    <span className="text-white">{loading ? "Verifying..." : "Enter Boutique"}</span>
                    {!loading && <ArrowRight size={14} className="text-white group-hover:translate-x-1 transition-transform" />}
                  </button>
                </form>
              )}

              {/* ✅ SIGNUP - Normal case inputs */}
              {mode === "signup" && (
                <form onSubmit={handleSignup} className="space-y-5">
                  <div className="space-y-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Full name"
                      value={signupData.name}
                      onChange={handleSignupChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={signupData.email}
                      onChange={handleSignupChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="tel"
                      name="phoneNo"
                      placeholder="Phone number"
                      value={signupData.phoneNo}
                      onChange={handleSignupChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="password"
                      name="password"
                      placeholder="Secure password"
                      value={signupData.password}
                      onChange={handleSignupChange}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 bg-black text-white text-xs font-black uppercase tracking-[0.3em] hover:bg-[var(--secondary)] transition-all duration-500 disabled:opacity-50"
                  >
                    <span className="text-white">{loading ? "Registering..." : "Confirm Identity"}</span>
                  </button>
                </form>
              )}

              {/* ✅ FORGOT PASSWORD - Normal case inputs */}
              {mode === "forgot" && (
                <form onSubmit={handleForgot} className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="text-xl font-black uppercase tracking-tighter text-black">
                      Access <span className="italic" style={{ color: "var(--secondary)" }}>Recovery</span>
                    </h3>
                    <p className="text-sm font-medium text-gray-600 leading-relaxed">
                      Enter your registry email to receive a recovery link.
                    </p>
                  </div>
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 bg-black text-white text-xs font-black uppercase tracking-[0.3em] hover:bg-[var(--secondary)] transition-all disabled:opacity-50"
                  >
                    <span className="text-white">{loading ? "Sending..." : "Request Reset"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-xs font-medium uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
                  >
                    Back to Login
                  </button>
                </form>
              )}

              {/* ✅ RESET PASSWORD - Normal case inputs */}
              {mode === "reset" && (
                <form onSubmit={handleReset} className="space-y-6">
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Reset code"
                      value={resetData.code}
                      onChange={(e) => setResetData((prev) => ({ ...prev, code: e.target.value }))}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="password"
                      placeholder="New password"
                      value={resetData.password}
                      onChange={(e) => setResetData((prev) => ({ ...prev, password: e.target.value }))}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      value={resetData.confirm}
                      onChange={(e) => setResetData((prev) => ({ ...prev, confirm: e.target.value }))}
                      className="w-full bg-transparent border-b-2 border-gray-400 py-3 outline-none focus:border-black text-sm font-medium placeholder:text-gray-400 text-black"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-5 bg-black text-white text-xs font-black uppercase tracking-[0.3em] hover:bg-[var(--secondary)] transition-all disabled:opacity-50"
                  >
                    <span className="text-white">{loading ? "Updating..." : "Update Credentials"}</span>
                  </button>
                </form>
              )}

              {/* ✅ Toast Container - Better visibility */}
              <ToastContainer
                position="bottom-center"
                autoClose={2500}
                hideProgressBar
                closeOnClick
                pauseOnHover={false} 
                draggable
                className="!relative !mt-6 !w-full"
                toastClassName="!rounded-none !border-l-4 !border-black !bg-white !shadow-lg !min-h-0 !p-4 !text-sm !font-medium !text-black !border-gray-200"
              />
            </motion.div>
          </AnimatePresence>

          {/* Decorative footer line - Enhanced contrast */}
          <div className="h-1.5 w-full flex">
            <div className="h-full w-1/4 bg-black" />
            <div className="h-full w-3/4" style={{ backgroundColor: 'var(--secondary)' }} />
          </div>
        </div>
      </div>
    </Dialog>
  );
}